/**
 * React hook that bridges the renderer-side AudioTranscriptionService
 * with the existing GlobalState / ASR lifecycle.
 *
 * Mounted once in AssistantHandlers (overlay). Watches for recording
 * or dictation state changes and starts / stops the service accordingly.
 * Works identically in Electron and browser runtimes.
 */
import { useEffect, useRef, useCallback } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import { GlobalState, GlobalStore } from '@highlight/ipc'
import audioTranscriptionService from '@renderer/services/audio-transcription-service'
import { useAudioFeaturesPermission } from '@renderer/hooks/settings/useAudioFeaturesPermission'

export function useAudioRuntime() {
  const serviceRunning = useRef(false)
  const pendingStopAbortRef = useRef<AbortController | null>(null)
  const [isAsrRunning] = useGlobalState<boolean>('asr.isRunning', false)
  const { areAudioFeaturesEnabled } = useAudioFeaturesPermission()

  const startService = useCallback(async (wantSystem: boolean) => {
    if (serviceRunning.current) return
    serviceRunning.current = true

    const locale = GlobalStore.settings().get<string>('locale', 'en-US') || 'en-US'
    const deviceName = GlobalStore.settings().get<string>('audiorecord.inputDevice', '')

    let micDeviceId: string | undefined
    if (deviceName) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const match = devices.find((d) => d.kind === 'audioinput' && d.label === deviceName)
        if (match) micDeviceId = match.deviceId
      } catch {
        // fall back to default
      }
    }

    try {
      await audioTranscriptionService.start({
        mic: true,
        system: wantSystem,
        locale,
        micDeviceId,
      })
    } catch (err) {
      console.error('[useAudioRuntime] Failed to start service:', err)
      serviceRunning.current = false
    }
  }, [])

  const stopService = useCallback(async () => {
    if (!serviceRunning.current) return
    serviceRunning.current = false
    try {
      await audioTranscriptionService.stop()
    } catch (err) {
      console.error('[useAudioRuntime] Failed to stop service:', err)
    }
  }, [])

  useEffect(() => {
    if (!areAudioFeaturesEnabled) {
      if (serviceRunning.current) {
        stopService()
      }
      return
    }

    if (isAsrRunning && !serviceRunning.current) {
      const isTakingNotes =
        GlobalState.get<{ status?: string }>('audio-note-state')?.status === 'recording' ||
        GlobalState.get<{ status?: string }>('audio-note-state')?.status === 'starting'
      startService(isTakingNotes)
    } else if (!isAsrRunning && serviceRunning.current) {
      stopService()
    }
  }, [isAsrRunning, areAudioFeaturesEnabled, startService, stopService])

  // Browser-only dictation bridge: when stopDictation sets a pending stop,
  // force a segment flush, await the final transcript, then collect accumulated
  // text and write it to the dictation atom so useDictatedText picks it up.
  const [pendingStop] = useGlobalState<string>('asr.browserDictationPendingStop', '')

  useEffect(() => {
    if (!pendingStop) return

    // Cancel any previous pending stop so a fast re-trigger doesn't double-write.
    pendingStopAbortRef.current?.abort()
    const abort = new AbortController()
    pendingStopAbortRef.current = abort

    const finish = () => {
      if (abort.signal.aborted) return
      const startTime = GlobalState.get<number>('asr.dictationStartTime', 0)
      const text = audioTranscriptionService.getTextForRange(0, startTime - 20, Date.now())
      if (text) {
        GlobalState.set(pendingStop + '.dictation', text)
      }
      GlobalState.delete('voiceMode.originAwaitingSegment')
      GlobalState.delete('asr.browserDictationPendingStop')
      GlobalState.set('asr.isRunning', false)
    }

    audioTranscriptionService
      .forceSegment(0)
      .then(finish)
      .catch((err) => {
        if (abort.signal.aborted) return
        console.error('[useAudioRuntime] forceSegment failed during dictation stop:', err)
        finish()
      })

    return () => {
      abort.abort()
    }
  }, [pendingStop])

  useEffect(() => {
    return () => {
      stopService()
    }
  }, [stopService])

  return {
    isServiceRunning: serviceRunning.current,
    getEnergy: (source: number) => audioTranscriptionService.getEnergy(source),
    getTextForRange: (source: number, start: number, end: number) =>
      audioTranscriptionService.getTextForRange(source, start, end),
    listDevices: () => audioTranscriptionService.listInputDevices(),
  }
}
